import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext"; 
import Header from "../../Common/Layout/Header"; 
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import SettingTabs from "./SettingsTabs";
import { useNavigate } from "react-router-dom";

const ManualInvoiceGenerator = () => {
   const { user, loading } = useAuth(); 
   const navigate = useNavigate();
  const contentRef = useRef();

  // --- 1. State for Vendor Info (Auto-populated from User Context) ---
  const [vendorDetails, setVendorDetails] = useState({
    companyName: user?.companyname || "Company Name",
    tagline: user?.brandName || "Travel Technology Solutions",
    address: user?.companyaddress || "Company Address",
    gstin: user?.companygstno || "",
    email: user?.email || "",
    phoneNumber: user?.companyphone || "",
    logo: user?.logo || null
  });


  // Sync state if user context loads later
  useEffect(() => {
    if (user) {
      setVendorDetails({
        companyName: user.companyname || "",
        tagline: user.brandName || "Travel Technology Solutions",
        gstin: user.companygstno || "",
        email: user.email || "",
        address: user.companyaddress || "",
        phoneNumber: user.companyphone || "",
        logo: user.logo || null
      });
    }
  }, [user]);
  
  useEffect(() => {
    if (!loading && !user) {
      navigate("/"); // Redirect to login if not authenticated
    }
  }, [user, loading, navigate]);

  // --- 2. State for Customer Info ---
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNo: "INV-" + Math.floor(1000 + Math.random() * 9000),
    date: moment().format("YYYY-MM-DD"),
    customerAgency: "",
    customerName: "",
    customerGst: "",
    customerAddress: "",
  });

  // --- 3. State for Payment Configuration ---
  const [paymentConfig, setPaymentConfig] = useState({
    method: "BANK", 
    bankName: "HDFC Bank",
    accountNo: "50200012345678",
    ifsc: "HDFC0001234",
    upiId: "pay@upi",
    paymentLink: "https://rzp.io/l/pay_now"
  });

  // --- 4. State for Editable Plans on the left ---
  const [availablePlans, setAvailablePlans] = useState([
    { id: 1, name: "Zotrai subscription plan", price: 2999, type: "per month" },
    { id: 2, name: "B2C website", price: 4999, type: "one time" },
    { id: 3, name: "B2B website", price: 4999, type: "one time" },
    { id: 4, name: "B2E website", price: 4999, type: "one time" },
  ]);

  const [newItem, setNewItem] = useState({ name: "", price: "" });

  // --- 5. State for Selected Items (The Invoice Table) ---
  const [selectedItems, setSelectedItems] = useState([]);

  // --- HANDLERS ---
  const handleVendorChange = (e) => setVendorDetails({ ...vendorDetails, [e.target.name]: e.target.value });
  const handlePaymentChange = (e) => setPaymentConfig({ ...paymentConfig, [e.target.name]: e.target.value });

  const handlePlanListChange = (id, field, value) => {
    const updated = availablePlans.map(p => p.id === id ? { ...p, [field]: field === 'price' ? Number(value) : value } : p);
    setAvailablePlans(updated);
  };

  const addItemToInvoice = (item) => {
    if (!item.name || !item.price) return;
    setSelectedItems([...selectedItems, { ...item, uniqueId: Date.now(), qty: 1 }]);
    if(item.id === 'custom') setNewItem({ name: "", price: "" });
  };

  const handleLiveEdit = (uniqueId, field, value) => {
    const updated = selectedItems.map(item => item.uniqueId === uniqueId ? { ...item, [field]: (field === 'price' || field === 'qty') ? Number(value) : value } : item);
    setSelectedItems(updated);
  };

  const removeItem = (id) => setSelectedItems(selectedItems.filter(item => item.uniqueId !== id));

  // Calculations
  const subtotal = selectedItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  const gst = subtotal * 0.18;
  const grandTotal = subtotal + gst;

 const handlePrint = useReactToPrint({
    contentRef: contentRef, // Latest version uses contentRef instead of content
    documentTitle: `Invoice_${invoiceDetails.invoiceNo}`,
  });

  // const handlePDF = () => {
  //   const input = contentRef.current;
  //   html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF('p', 'mm', 'a4');
  //     const imgWidth = 210;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;
  //     pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  //     pdf.save(`Invoice_${invoiceDetails.invoiceNo}.pdf`);
  //   });
  // };

  //Quality Image PDF Generation
  const handlePDF = () => {
  const input = contentRef.current;

  // 1. Increase scale to 4 for high-definition (Retina) resolution
  // 2. logging: false makes it faster
  // 3. useCORS: true ensures external logos load
  html2canvas(input, { 
    scale: 4, 
    useCORS: true, 
    logging: false,
    backgroundColor: '#ffffff', // Ensures a crisp white background
    windowWidth: input.scrollWidth,
    windowHeight: input.scrollHeight
  }).then((canvas) => {
    
    // Convert to PNG with maximum quality (1.0)
    const imgData = canvas.toDataURL("image/png", 1.0);
    
    // Create PDF with high precision (points instead of mm often yields better results)
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    
    // Calculate the width and height to fit perfectly on the A4 page
    const ratio = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight);
    const imgWidth = canvasWidth * ratio;
    const imgHeight = canvasHeight * ratio;
    
    // Center the image on the page
    const xOffset = (pdfWidth - imgWidth) / 2;
    
    // addImage(data, format, x, y, width, height, alias, compression)
    // Using 'NONE' or 'FAST' alias preserves the sharpness of the 4x scale
    pdf.addImage(imgData, "PNG", xOffset, 0, imgWidth, imgHeight, undefined, 'FAST');
    
    pdf.save(`Invoice_${invoiceDetails.invoiceNo}.pdf`);
  });
};

  const styles = {
    invoiceContainer: { padding: '40px', backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif' },
    topHeader: { display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '20px', marginBottom: '20px', alignItems: 'center' },
    addressSection: { display: 'flex', justifyContent: 'space-between', marginBottom: '30px' },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: '30px' },
    th: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', padding: '12px', textAlign: 'left', fontWeight: 'bold', fontSize: '12px' },
    td: { padding: '10px', borderBottom: '1px solid #eee' },
    editableInput: { border: 'none', background: 'transparent', width: '100%', outline: 'none', padding: '0' },
    totalSection: { float: 'right', width: '250px' },
    totalRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0' }
  };
   if (loading) return <div className="p-5 text-center"><h5>Loading...</h5></div>;
  if (!user) return null; // Redirect logic is in useEffect

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="invoices"/>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
             <div className="p-2">
                                                              <SettingTabs pageName="MANUALINVOICE" />
                                                            </div>
            <div className="row mb-3 align-items-center">
                <div className="col"><h4>Manual Invoice Generator</h4></div>
                <div className="col-auto">
                    <button className="btn btn-info me-2 shadow-none" onClick={handlePrint}><i className="ri-printer-line"></i> Print</button>
                    <button className="btn btn-primary shadow-none" onClick={handlePDF}><i className="ri-file-download-line"></i> Download PDF</button>
                </div>
            </div>

            <div className="row">
              {/* --- LEFT SIDE: ADMIN CONTROLS --- */}
              <div className="col-lg-5" style={{maxHeight: '85vh', overflowY: 'auto'}}>
                
                {/* 1. VENDOR CONFIG */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header bg-soft-dark fw-bold">1. Vendor Details (From)</div>
                  <div className="card-body">
                    <div className="row g-2">
                        <div className="col-12"><input className="form-control form-control-sm" name="companyName" placeholder="Company Name" value={vendorDetails.companyName} onChange={handleVendorChange} /></div>
                        <div className="col-12"><input className="form-control form-control-sm" name="tagline" placeholder="Tagline" value={vendorDetails.tagline} onChange={handleVendorChange} /></div>
                        <div className="col-6"><input className="form-control form-control-sm" name="gstin" placeholder="GSTIN" value={vendorDetails.gstin} onChange={handleVendorChange} /></div>
                        <div className="col-6"><input className="form-control form-control-sm" name="email" placeholder="Support Email" value={vendorDetails.email} onChange={handleVendorChange} /></div>
                        <div className="col-12"><input className="form-control form-control-sm" name="phoneNumber" placeholder="Support Phone" value={vendorDetails.phoneNumber} onChange={handleVendorChange} /></div>
                        <div className="col-12"><textarea className="form-control form-control-sm" name="address" placeholder="Company Address" value={vendorDetails.address} onChange={handleVendorChange} rows="2" /></div>
                    </div>
                  </div>
                </div>

                {/* 2. CUSTOMER CONFIG */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header bg-soft-primary fw-bold">2. Customer Info (Bill To)</div>
                  <div className="card-body">
                    <div className="row g-2">
                        <div className="col-6"><input className="form-control form-control-sm" placeholder="Invoice No" value={invoiceDetails.invoiceNo} onChange={(e)=>setInvoiceDetails({...invoiceDetails, invoiceNo: e.target.value})} /></div>
                        <div className="col-6"><input type="date" className="form-control form-control-sm" value={invoiceDetails.date} onChange={(e)=>setInvoiceDetails({...invoiceDetails, date: e.target.value})} /></div>
                        <div className="col-12"><input className="form-control form-control-sm" placeholder="Agency Name" value={invoiceDetails.customerAgency} onChange={(e)=>setInvoiceDetails({...invoiceDetails, customerAgency: e.target.value})} /></div>
                        <div className="col-12"><input className="form-control form-control-sm" placeholder="Client GSTIN" value={invoiceDetails.customerGst} onChange={(e)=>setInvoiceDetails({...invoiceDetails, customerGst: e.target.value})} /></div>
                        <div className="col-12"><textarea className="form-control form-control-sm" placeholder="Address" value={invoiceDetails.customerAddress} onChange={(e)=>setInvoiceDetails({...invoiceDetails, customerAddress: e.target.value})} rows="2" /></div>
                    </div>
                  </div>
                </div>

                {/* 3. ITEM SELECTION */}
                <div className="card shadow-sm mb-3">
                  <div className="card-header bg-soft-success fw-bold">3. Items & Pricing</div>
                  <div className="card-body p-0">
                    <table className="table table-sm mb-0">
                        <thead className="table-light">
                            <tr><th>Item Name</th><th width="100">Price</th><th>Add</th></tr>
                        </thead>
                        <tbody>
                            {availablePlans.map((plan) => (
                                <tr key={plan.id}>
                                    <td><input className="form-control form-control-sm border-0" value={plan.name} onChange={(e)=>handlePlanListChange(plan.id, 'name', e.target.value)} /></td>
                                    <td><input type="number" className="form-control form-control-sm border-0" value={plan.price} onChange={(e)=>handlePlanListChange(plan.id, 'price', e.target.value)} /></td>
                                    <td className="text-center"><button className="btn btn-sm btn-success py-0 px-2" onClick={() => addItemToInvoice(plan)}>+</button></td>
                                </tr>
                            ))}
                            <tr className="table-info">
                                <td><input className="form-control form-control-sm" placeholder="Custom..." value={newItem.name} onChange={(e)=>setNewItem({...newItem, name: e.target.value})} /></td>
                                <td><input type="number" className="form-control form-control-sm" placeholder="0" value={newItem.price} onChange={(e)=>setNewItem({...newItem, price: e.target.value})} /></td>
                                <td className="text-center"><button className="btn btn-sm btn-dark py-0 px-2" onClick={() => addItemToInvoice({id:'custom', name: newItem.name, price: Number(newItem.price)})}>Add</button></td>
                            </tr>
                        </tbody>
                    </table>
                  </div>
                </div>

                {/* 4. PAYMENT CONFIG */}
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-soft-warning fw-bold">4. Payment Information</div>
                  <div className="card-body">
                    <div className="row g-2">
                        <div className="col-12 mb-2">
                            <label className="small fw-bold">Method</label>
                            <select className="form-select form-select-sm" name="method" value={paymentConfig.method} onChange={handlePaymentChange}>
                                <option value="BANK">Bank Transfer</option>
                                <option value="UPI">UPI</option>
                                <option value="RAZORPAY">Razorpay</option>
                            </select>
                        </div>
                        {paymentConfig.method === "BANK" && (
                            <><div className="col-12"><input className="form-control form-control-sm" name="bankName" placeholder="Bank" value={paymentConfig.bankName} onChange={handlePaymentChange} /></div>
                              <div className="col-12"><input className="form-control form-control-sm" name="accountNo" placeholder="A/c No" value={paymentConfig.accountNo} onChange={handlePaymentChange} /></div>
                              <div className="col-12"><input className="form-control form-control-sm" name="ifsc" placeholder="IFSC" value={paymentConfig.ifsc} onChange={handlePaymentChange} /></div></>
                        )}
                        {paymentConfig.method === "UPI" && (<div className="col-12"><input className="form-control form-control-sm" name="upiId" placeholder="UPI ID" value={paymentConfig.upiId} onChange={handlePaymentChange} /></div>)}
                        {paymentConfig.method === "RAZORPAY" && (<div className="col-12"><input className="form-control form-control-sm" name="paymentLink" placeholder="Link" value={paymentConfig.paymentLink} onChange={handlePaymentChange} /></div>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- RIGHT SIDE: LIVE PREVIEW --- */}
              <div className="col-lg-7">
                <div className="card shadow-lg border-0">
                  <div className="card-body p-0">
                    <div ref={contentRef} style={styles.invoiceContainer}>
                      
                      {/* Vendor Header (DYNAMIC LOGO) */}
                      <div style={styles.topHeader}>
                        <div>
                          {vendorDetails.logo ? (
                             <img src={vendorDetails.logo} alt="Logo" style={{maxHeight: '60px', maxWidth: '180px', marginBottom: '10px', display:'block'}} />
                          ) : (
                             <h2 style={{margin:0, color: '#0ab39c', fontWeight: 'bold'}}>{vendorDetails.companyName}</h2>
                          )}
                          <p style={{fontSize: '14px', color:'#333', margin: '2px 0', fontWeight: 'bold'}}>{vendorDetails.companyName}</p>
                          <p style={{fontSize: '14px', color:'#333', margin: '2px 0', fontWeight: 'bold'}}>{vendorDetails.tagline}</p>
        
                          <p style={{fontSize: '12px', color:'#666', margin:0}}>GSTIN: {vendorDetails.gstin} </p>
                          <p style={{fontSize: '12px', color:'#666', margin:0}}>{vendorDetails.email} | {vendorDetails.phoneNumber}</p>
                            <p style={{fontSize: '12px', color:'#666', margin:0}}>{vendorDetails.address}</p>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <h3 style={{margin:0, textTransform:'uppercase', letterSpacing:'1px'}}>Invoice</h3>
                          <p style={{margin:0, fontSize: '14px'}}># {invoiceDetails.invoiceNo}</p>
                          <p style={{margin:0, fontSize: '14px'}}>{moment(invoiceDetails.date).format("DD MMM YYYY")}</p>
                        </div>
                      </div>

                      {/* Bill To Section */}
                      <div style={styles.addressSection}>
                        <div>
                          <p style={{fontSize: '10px', fontWeight:'bold', marginBottom:'3px', color: '#888'}}>BILL TO</p>
                          <h6 style={{margin:0, fontWeight:'bold', fontSize:'15px'}}>{invoiceDetails.customerAgency || "Agency Name"}</h6>
                          <p style={{margin:0, fontSize:'12px', width: '280px'}}>{invoiceDetails.customerAddress || "Address..."}</p>
                          <p style={{margin:0, fontSize:'12px', fontWeight:'bold'}}>GSTIN: {invoiceDetails.customerGst || "N/A"}</p>
                        </div>
                      </div>

                      {/* Table */}
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>DESCRIPTION</th>
                            <th style={{...styles.th, textAlign:'right'}} width="120">PRICE</th>
                            <th style={{...styles.th, textAlign:'center'}} width="80">QTY</th>
                            <th style={{...styles.th, textAlign:'right'}} width="120">TOTAL</th>
                            <th className="no-print" width="40"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItems.map((item) => (
                            <tr key={item.uniqueId}>
                                <td style={styles.td}><input style={{...styles.editableInput, fontWeight:'bold'}} value={item.name} onChange={(e)=>handleLiveEdit(item.uniqueId, 'name', e.target.value)} /></td>
                                <td style={{...styles.td, textAlign:'right'}}><input type="number" style={{...styles.editableInput, textAlign:'right'}} value={item.price} onChange={(e)=>handleLiveEdit(item.uniqueId, 'price', e.target.value)} /></td>
                                <td style={{...styles.td, textAlign:'center'}}><input type="number" style={{...styles.editableInput, textAlign:'center'}} value={item.qty} onChange={(e)=>handleLiveEdit(item.uniqueId, 'qty', e.target.value)} /></td>
                                <td style={{...styles.td, textAlign:'right', fontWeight:'bold'}}>{(item.price * item.qty).toLocaleString()}</td>
                                <td className="no-print text-center" style={{borderBottom:'1px solid #eee'}}>
                                    <button className="btn btn-link text-danger p-0" onClick={() => removeItem(item.uniqueId)}><i className="ri-delete-bin-line"></i></button>
                                </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Totals */}
                      <div className="clearfix">
                        <div style={styles.totalSection}>
                            <div style={styles.totalRow}><span style={{color: '#666'}}>Sub Total:</span><span>INR {subtotal.toLocaleString()}</span></div>
                            <div style={styles.totalRow}><span style={{color: '#666'}}>GST (18%):</span><span>INR {gst.toLocaleString()}</span></div>
                            <div style={{...styles.totalRow, borderTop: '2px solid #333', marginTop: '10px', paddingTop: '10px', fontWeight:'bold', fontSize:'18px'}}>
                                <span>Grand Total:</span><span style={{color: '#0ab39c'}}>INR {grandTotal.toLocaleString()}</span>
                            </div>
                        </div>
                      </div>

                      {/* DYNAMIC PAYMENT INFO */}
                      <div style={{marginTop: '150px', borderTop: '1px solid #eee', paddingTop: '20px'}}>
                        <div className="row">
                            <div className="col-7">
                                <p style={{fontSize:'11px', fontWeight:'bold', marginBottom: '8px', color:'#555', textTransform:'uppercase'}}>Payment Information:</p>
                                {paymentConfig.method === "BANK" && (
                                    <><p style={{fontSize:'12px', margin:0, fontWeight:'bold'}}>{paymentConfig.bankName}</p>
                                      <p style={{fontSize:'12px', margin:0}}>A/c: {paymentConfig.accountNo}</p>
                                      <p style={{fontSize:'12px', margin:0}}>IFSC: {paymentConfig.ifsc}</p></>
                                )}
                                {paymentConfig.method === "UPI" && (<p style={{fontSize:'12px', margin:0}}>UPI ID: {paymentConfig.upiId}</p>)}
                                {paymentConfig.method === "RAZORPAY" && (<p style={{fontSize:'11px', margin:0, color:'#0ab39c'}}>{paymentConfig.paymentLink}</p>)}
                            </div>
                            {/* <div className="col-5 text-center">
                                <div style={{height: '50px'}}></div>
                                <p style={{fontSize:'12px', borderTop: '1px solid #000', display:'inline-block', padding:'5px 20px'}}>Authorized Signatory</p>
                            </div> */}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <style>{`
        input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        @media print { .no-print { display: none !important; } }
      `}</style>
    </div>
  );
};

export default ManualInvoiceGenerator;